import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/EmptyState";

type ActivityEntry = {
  id: string;
  action: string;
  description: string | null;
  created_at: string;
};

const RecentActivity = () => {
  const [activities, setActivities] = useState<ActivityEntry[]>([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data } = await supabase
        .from("system_logs")
        .select("id, action, description, created_at")
        .order("created_at", { ascending: false })
        .limit(5);
      if (active) setActivities(data ?? []);
    };
    load();
    return () => { active = false; };
  }, []);

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
      {activities.length === 0 ? (
        <EmptyState icon={Clock} title="No recent activity" description="Your recent events will appear here as you use the platform." />
      ) : (
        <div className="space-y-4">
          {activities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-3 border-b border-border pb-3 last:border-0 last:pb-0">
              <Clock className="mt-1 h-4 w-4 shrink-0 text-accent" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{activity.action}</p>
                {activity.description && <p className="text-sm text-muted-foreground break-words">{activity.description}</p>}
              </div>
              <time className="shrink-0 text-xs text-muted-foreground" dateTime={activity.created_at}>
                {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
              </time>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default RecentActivity;